<template lang="pug">
el-dialog(
  title="Select or Create Question"
  top="5vh"
  width="60%"
  class="el-dialog__max-w-700"
  :close-on-click-modal="false"
  v-model="dialogVisible"
)
  .questions-selector
    .create-new-section
      el-form(ref="createFormRef" :model="createForm" :rules="createRules" label-position="top")
        el-form-item(label="Create New Question" prop="content")
          .create-question-input
            el-input(
              v-model="createForm.content"
              placeholder="Enter new question content"
              @keyup.enter="handleCreateQuestion"
            )
            el-button(
              type="primary"
              @click="handleCreateQuestion"
              :loading="creating"
              size="default"
            ) Create

    .divider-section
      el-divider
        span Or Select Existing

    .questions-selector-header
      el-input(
        v-model="searchTerm"
        placeholder="Search questions"
        @input="debouncedSearch"
        clearable
      )
        template(#suffix)
          fai(type="fal" icon="search")

    .questions-list(v-loading="loading")
      .empty-state(v-if="!questions.length && !loading")
        p No questions found

      .questions-list-view(v-else)
        .question-list-item(
          v-for="question in questions"
          :key="question.id"
          :class="{ 'selected': selectedQuestion === question.id }"
          @click="selectQuestion(question.id)"
        )
          .question-list-item-content
            .question-text {{ question.content }}
            .question-date {{ formatDate(question.createdAt) }}

    .pagination-controls(v-if="questions.length")
      .simple-pagination.d-flex.align-items-center
        .page-nav.d-flex
          el-button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
            size="small"
          ) Prev
          .page-numbers.mx-2 {{ currentPage }}
          el-button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
            size="small"
          ) Next
        .page-info.ms-3(v-if="questions.length > 0")
          | Showing {{ startItem }}-{{ endItem }}

  template(#footer)
    .dialog-footer
      el-button(@click="handleCancel") Cancel
      el-button(type="primary" @click="handleConfirm" :disabled="!selectedQuestion") Confirm
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted,
} from 'vue';
import { ElMessage } from 'element-plus';
import { findAllFeaturedQuestions, mutateFeaturedQuestion } from '@/graphs/gql';
import { makeRequest } from '@/graphs/index';
import globalShared from '@/shared';

// Using a simple debounce implementation
function debounce(func: Function, wait: number) {
  let timeout: NodeJS.Timeout;
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default defineComponent({
  name: 'DialogSelectSingleQuestion',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    preSelectedQuestion: {
      type: String,
      default: '',
    },
    orgId: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'confirm', 'clear'],
  setup(props, { emit }) {
    const { makeGraphRequest, getAuthToken } = globalShared();

    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const questions = ref([]);
    const loading = ref(false);
    const creating = ref(false);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);
    const selectedQuestion = ref(props.preSelectedQuestion || '');
    const createFormRef = ref();

    const createForm = ref({
      content: '',
    });

    const createRules = {
      content: [
        { required: true, message: 'Please enter question content', trigger: 'blur' },
        { min: 3, message: 'Question must be at least 3 characters', trigger: 'blur' },
      ],
    };

    // Define fetchQuestions function
    const fetchQuestions = async () => {
      loading.value = true;
      try {
        const skip = (currentPage.value - 1) * pageSize.value;
        const result = await makeRequest(
          'query',
          findAllFeaturedQuestions,
          {
            orgId: props.orgId || null,
            take: pageSize.value,
            skip,
          },
          null,
        );

        if (result?.data?.findAllFeaturedQuestions) {
          let fetchedQuestions = result.data.findAllFeaturedQuestions;

          // Filter by search term if provided
          if (searchTerm.value) {
            fetchedQuestions = fetchedQuestions.filter((q: any) => q.content
              .toLowerCase()
              .includes(searchTerm.value.toLowerCase()));
          }

          questions.value = fetchedQuestions;
          hasMorePages.value = fetchedQuestions.length === pageSize.value;
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
        ElMessage.error('Failed to load questions');
      } finally {
        loading.value = false;
      }
    };

    // Handle creating a new question
    const handleCreateQuestion = async () => {
      if (!createFormRef.value) return;

      try {
        await createFormRef.value.validate();
        creating.value = true;

        const response = await makeGraphRequest(
          'mutation',
          mutateFeaturedQuestion,
          {
            orgId: props.orgId || null,
            input: {
              content: createForm.value.content,
            },
          },
          getAuthToken(),
        );

        if (response?.data?.mutateFeaturedQuestion) {
          const newQuestion = response.data.mutateFeaturedQuestion;
          ElMessage.success('Question created successfully!');

          // Select the newly created question
          selectedQuestion.value = newQuestion.id;

          // Add to the list at the top
          questions.value.unshift(newQuestion);

          // Clear the form
          createForm.value.content = '';

          // Emit confirm with the new question
          emit('confirm', newQuestion.id, newQuestion);
          dialogVisible.value = false;
        }
      } catch (error) {
        console.error('Error creating question:', error);
        ElMessage.error('Failed to create question');
      } finally {
        creating.value = false;
      }
    };

    // Watch for dialog visibility to fetch data and reset selections when opened
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        selectedQuestion.value = props.preSelectedQuestion || '';
        createForm.value.content = '';
        fetchQuestions();
      }
    });

    // Watch for changes to preSelectedQuestion
    watch(() => props.preSelectedQuestion, (newVal) => {
      selectedQuestion.value = newVal || '';
    });

    const debouncedSearch = debounce(() => {
      currentPage.value = 1;
      hasMorePages.value = true;
      fetchQuestions();
    }, 300);

    const handleCurrentChange = (page: number) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      fetchQuestions();
    };

    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + questions.value.length;
    });

    const selectQuestion = (id: string) => {
      selectedQuestion.value = id;
    };

    const handleConfirm = () => {
      const selectedQuestionObject = questions.value.find((q: any) => q.id === selectedQuestion.value);
      emit('confirm', selectedQuestion.value, selectedQuestionObject);
      dialogVisible.value = false;
    };

    const handleCancel = () => {
      dialogVisible.value = false;
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    onMounted(() => {
      if (props.modelValue) {
        fetchQuestions();
      }
    });

    return {
      dialogVisible,
      questions,
      loading,
      creating,
      searchTerm,
      currentPage,
      pageSize,
      hasMorePages,
      selectedQuestion,
      createFormRef,
      createForm,
      createRules,
      startItem,
      endItem,
      debouncedSearch,
      handleCurrentChange,
      selectQuestion,
      handleConfirm,
      handleCancel,
      handleCreateQuestion,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.questions-selector {
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.create-new-section {
  margin-bottom: 16px;
}

.create-question-input {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.divider-section {
  margin: 8px 0;
}

.questions-selector-header {
  margin-bottom: 20px;
}

.questions-list {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.questions-list-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-list-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
}

.question-list-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.question-list-item.selected {
  border-color: #1EBEAF;
  background-color: rgba(30, 190, 175, 0.05);
}

.question-list-item-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.question-text {
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.question-date {
  font-size: 12px;
  color: #909399;
}

.pagination-controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.simple-pagination {
  display: flex;
  align-items: center;
}

.page-nav {
  display: flex;
  align-items: center;
}

.page-numbers {
  margin: 0 10px;
  font-weight: bold;
}

.page-info {
  margin-left: 15px;
  color: #606266;
  font-size: 0.9em;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}
</style>

